import React from 'react'
import { RouteObject, Navigate, useParams } from 'react-router-dom'

import { AccessGuard, CustomerLoginGuard } from '@guards'
import { MainLayout, HomeLayout, ForumRedirect } from '@components/systems'

// home
import CustomerLoginPage from '@pages/home/monolith/CustomerLogin.page'
import CustomerJoinPage from '@pages/home/monolith/CustomerJoin.page'
import CustomerForgotPwPage from '@pages/home/monolith/CustomerForgotPw.page'
import CustomerInfoPage from '@pages/home/monolith/CustomerInfo.page'
import CustomerOrderPage from '@pages/home/monolith/CustomerOrder.page'
import CustomerInquiryPage from '@pages/home/monolith/CustomerInquiry.page'
import CustomerWholesalePage from '@pages/home/monolith/CustomerWholesale.page'
import CustomerPerformancePage from '@pages/home/monolith/CustomerPerformance.page'

// 2026-09-08 홈페이지 2안(Monolith) 적용 — 기존 페이지 파일은 보관, 라우트만 교체.
// 원복하려면 아래 네 줄을 기존 경로로 되돌린다:
//   @pages/home/Home.page · Shop.page · ShopKoinosPls8.page · Contact.page
import HomePage from '@pages/home/monolith/Home.page'
import ShopPage from '@pages/home/monolith/Shop.page'
import ShopKoinosPls8Page from '@pages/home/monolith/Pls8.page'
import ContactPage from '@pages/home/monolith/Contact.page'
import SupportPage from '@pages/home/monolith/Support.page'
import SoftwarePage from '@pages/home/monolith/Software.page'
import SoftwareDetailPage from '@pages/home/monolith/SoftwareDetail.page'
// 2026-09-09 사이트맵 §9 — Products(라인업)/Shop(구매) 분리, Forum·Works with·FAQ 신설 (judahsWeb/SITEMAP_PLAN.md)
import ProductsPage from '@pages/home/monolith/Products.page'
import WorksWithPage from '@pages/home/monolith/WorksWith.page'
import FaqPage from '@pages/home/monolith/Faq.page'
// About 도 Monolith 로 교체(2026-09-09). 구 페이지는 @pages/home/About.page 에 보관 — 본문이 경쟁사 카피였다.
import AboutPage from '@pages/home/monolith/About.page'
// 2026-09-09 /products(제품) 와 /shop(구매) 의 상세를 분리했다 (SITEMAP_PLAN §14·§16).
//   /products/{code} = 이 제품이 무엇인가. 랜딩 겸 정본 상세, 색인 대상.
//   /shop/{code}     = 이걸 산다. 가격·옵션·담기. 5종 전부 같은 템플릿이다.
import ProductDetailPage from '@pages/home/monolith/ProductDetail.page'
// 2026-09-09 구매 상세 4종 Monolith 전환 — 한 템플릿 + configs/shopDetail.config.ts.
// 구 디자인 4종(@pages/home/Shop{Kedem6,Kedem4,AirMidiPog2,DualSwitch}.page)은 보관만 한다.
import ShopDetailPage from '@pages/home/monolith/ShopDetail.page'
// 포럼 DiscourseConnect 착지점 (FORUM_PLAN §11.7). 2026-09-15: 페이지 파일만 있고 라우트가 빠져 있어 운영 /sso 가 404 였다.
import DiscourseSsoPage from '@pages/home/monolith/DiscourseSso.page'
// 2026-09-10 거래·정책·404 도 Monolith 로 교체 (SITEMAP_PLAN §18). 구 페이지 파일은 보관만 한다:
//   @pages/home/{NotFound,Cart,Payment,PaymentSuccess,PrivacyPolicy,StorePolicy}.page
import NotFoundPage from '@pages/home/monolith/NotFound.page'
import CartPage from '@pages/home/monolith/Cart.page'
import PaymentPage from '@pages/home/monolith/Payment.page'
import PaymentSuccessPage from '@pages/home/monolith/PaymentSuccess.page'

// 2026-09-08 Manuals 도 Monolith 로 교체. 원복: @pages/home/Manuals.page
import ManualsPage from '@pages/home/monolith/Manuals.page'

// 2026-09-09 /event 라우트 제거 (SITEMAP_PLAN §9). 페이지 파일은 보관.

import PrivacyPolicyPage from '@pages/home/monolith/PrivacyPolicy.page'
import StorePolicyPage from '@pages/home/monolith/StorePolicy.page'

// main
import MainHomePage from '@pages/main/home/Home.page'
import ProductPage from '@pages/main/Product.page'
import ItemPage from '@pages/main/Item.page'
import SeasonPage from '@pages/main/Season.page'
import SeasonProductDetailPage from '@pages/main/SeasonProductDetail.page'
import OrderPage from '@pages/main/Order.page'
import CustomerPage from '@pages/main/Customer.page'
import CouponPage from '@pages/main/Coupon.page'
import ResetMainCustomerPasswordPage from '@pages/main/ResetMainCustomerPassword.page'
import InquiryManagementPage from '@pages/main/InquiryManagement.page'
import BoxPage from '@pages/main/Box.page'
import DealerPricePage from '@pages/main/DealerPrice.page'
import SettlementPage from '@pages/main/Settlement.page'
import ShippingPage from '@pages/main/Shipping.page'
// 백오피스 계정(USERS) 본인 정보. 페이지는 @pages/admin 에 있지만 /main 과 /admin 양쪽에서 연다.
import AdminMyPage from '@pages/admin/AdminMyPage.page'

/** 옛 대화 주소 /customer/messages/:id → /customer/inquiry/:id */
const LegacyMessagesRedirect = () => {
	const { inquiryId } = useParams<{ inquiryId: string }>()
	return (
		<Navigate to={`/customer/inquiry/${encodeURIComponent(inquiryId ?? '')}`} replace />
	)
}

/* eslint-disable */
const HomeRoute = (mainUriList: { [id: string]: string }): RouteObject[] => {
	const res = [
		{
			path: '/',
			element: (
				<CustomerLoginGuard>
					<HomeLayout />
				</CustomerLoginGuard>
			),
			children: [
				{
					// 홈 리다이렉트에서 404 페이지로 바꿨다. CloudFront 가 없는 경로까지
					// 200 + index.html 을 주기 때문에(PENDING_CHANGES 8번), 리다이렉트만으로는
					// 크롤러가 "홈과 같은 페이지가 무한히 있는" 것으로 본다.
					path: '*',
					element: <NotFoundPage />,
				},
				{
					path: '/',
					element: <HomePage />,
				},
				{
					path: '/customer/login',
					element: <CustomerLoginPage />,
				},
				{
					path: '/customer/join',
					element: <CustomerJoinPage />,
				},
				{
					path: '/customer/forgot-password',
					element: <CustomerForgotPwPage />,
				},
				{
					path: '/customer/info',
					element: <CustomerInfoPage />,
				},
				{
					path: '/customer/order',
					element: <CustomerOrderPage />,
				},
				// 주문 자세히 — 주문마다 자기 주소. 같은 페이지가 전체 화면 모달로 연다(2026-10-06)
				{
					path: '/customer/order/:orderId',
					element: <CustomerOrderPage />,
				},
				// My Inquiries — 목록과 대화 한 건이 같은 페이지다. 대화 주소는 답변 메일의 링크가 온다
				{
					path: '/customer/inquiry',
					element: <CustomerInquiryPage />,
				},
				{
					path: '/customer/inquiry/:inquiryId',
					element: <CustomerInquiryPage />,
				},
				// 옛 주소(2026-10-06 하루 운영) — 그날 나간 답변 메일의 링크가 여기로 온다
				{
					path: '/customer/messages',
					element: <Navigate to="/customer/inquiry" replace />,
				},
				{
					path: '/customer/messages/:inquiryId',
					element: <LegacyMessagesRedirect />,
				},
				// Change Password · Log out 은 My Info 안으로 옮겼다(2026-10-06). 비밀번호 변경은 My Info 위 전체 화면 모달,
				// 로그아웃은 My Info 의 버튼 → 확인 모달. 옛 주소는 그쪽으로 넘긴다
				{
					path: '/customer/info/password',
					element: <CustomerInfoPage />,
				},
				{
					path: '/customer/change-password',
					element: <Navigate to="/customer/info/password" replace />,
				},
				{
					path: '/customer/logout',
					element: <Navigate to="/customer/info" replace />,
				},
				// 역할 계정 페이지 (PARTNER_PLAN §2) — 역할 검사는 CustomerLoginGuard
				{
					path: '/customer/wholesale',
					element: <CustomerWholesalePage />,
				},
				// 도매 주문 자세히 — 주문마다 자기 주소. 같은 페이지가 전체 화면 모달로 연다(2026-10-06)
				{
					path: '/customer/wholesale/:orderId',
					element: <CustomerWholesalePage />,
				},
				{
					path: '/customer/performance',
					element: <CustomerPerformancePage />,
				},
				// {
				// 	path: '/pedals',
				// 	element: <PedalsPage />,
				// },
				{
					path: '/support',
					element: <SupportPage />,
				},
				{
					// 소프트웨어 목록·상세 (PENDING_CHANGES §12). 상세는 매뉴얼에 인쇄되는 정식 주소.
					path: '/software',
					element: <SoftwarePage />,
				},
				{
					path: '/software/:slug',
					element: <SoftwareDetailPage />,
				},
				{
					// 구매 한 장. 제품 설명은 /products 에 있다.
					path: '/shop',
					element: <ShopPage />,
				},
				{
					// 라인업 인덱스 — Koinos · Kedem · Utility 세 행. 라인 페이지는 두지 않는다(앵커).
					path: '/products',
					element: <ProductsPage />,
				},
				// --- 제품 소개 /products/{code} ---
				{
					// 출시 전 티저. 색인에 시간이 걸려서 미리 올린다 (SEO_PLAN.md §4)
					path: '/products/pls8',
					element: <ShopKoinosPls8Page />,
				},
				{
					path: '/products/kdm6',
					element: <ProductDetailPage code="KDM6" />,
				},
				{
					path: '/products/kdm4',
					element: <ProductDetailPage code="KDM4" />,
				},
				{
					path: '/products/amp2',
					element: <ProductDetailPage code="AMP2" />,
				},
				{
					path: '/products/ds',
					element: <ProductDetailPage code="DS" />,
				},

				// --- 구매 /shop/{code} ---
				// 2026-09-09: `/shop/{code}` → `/products/{code}` 리다이렉트를 걷어냈다. 이 주소가
				// 다시 실제 페이지다 — Reverb·PedalPedia 역링크와 옛 색인이 그대로 착지한다.
				{
					// 값이 정해지기 전이라 구매 패널이 Register interest 로 내려앉는다
					path: '/shop/pls8',
					element: <ShopDetailPage code="PLS8" />,
				},
				{
					path: '/shop/kdm6',
					element: <ShopDetailPage code="KDM6" />,
				},
				{
					path: '/shop/kdm4',
					element: <ShopDetailPage code="KDM4" />,
				},
				{
					path: '/shop/amp2',
					element: <ShopDetailPage code="AMP2" />,
				},
				{
					path: '/shop/ds',
					element: <ShopDetailPage code="DS" />,
				},
				{
					// 2026-09-10 포럼 오픈 (FORUM_PLAN §14.9 Phase 5) — 빈 페이지를 걷어내고
					// forum.judahs.io 로 넘긴다. 옛 링크·북마크가 착지할 자리로만 남긴다.
					path: '/forum',
					element: <ForumRedirect />,
				},
				{
					path: '/works-with',
					element: <WorksWithPage />,
				},
				{
					path: '/faq',
					element: <FaqPage />,
				},
				{
					path: '/about',
					element: <AboutPage />,
				},
				{
					path: '/contact',
					element: <ContactPage />,
				},
				{
					path: '/cart',
					element: <CartPage />,
				},
				{
					path: '/payment',
					element: <PaymentPage />,
				},
				{
					path: '/payment-success',
					element: <PaymentSuccessPage />,
				},
				{
					path: '/privacy-policy',
					element: <PrivacyPolicyPage />,
				},
				{
					path: '/store-policy',
					element: <StorePolicyPage />,
				},
				{
					path: '/manuals',
					element: <ManualsPage />,
				},
			],
		},
		{
			// HomeLayout 밖 형제 라우트 — 헤더·푸터를 그렸다 지우지 않고, 로그인 가드도 타지 않는다.
			// 토큰이 없으면 페이지가 스스로 /customer/login 으로 보낸다.
			path: '/sso',
			element: <DiscourseSsoPage />,
		},
		{
			path: '/main',
			element: (
				<AccessGuard>
					<MainLayout />
				</AccessGuard>
			),
			children: [
				{
					path: '/main/empty',
					element: <></>,
				},
				{
					path: '/main/home',
					element: <MainHomePage />,
				},
				{
					path: '/main/product',
					element: <ProductPage />,
				},
				{
					path: '/main/item',
					element: <ItemPage />,
				},
				{
					path: '/main/season',
					element: <SeasonPage />,
				},
				{
					path: '/main/order',
					element: <OrderPage />,
				},
				{
					// 주문 상세 — 목록 위에 덮이는 전체 화면 한 장(OrderDetailModal).
					// 같은 페이지를 그리고 URL 의 주문번호만 모달에 넘긴다. 새로고침·링크 공유가 된다.
					path: '/main/order/:orderId',
					element: <OrderPage />,
				},
				{
					path: '/main/customer',
					element: <CustomerPage />,
				},

				{
					path: '/main/coupon',
					element: <CouponPage />,
				},
				{
					path: '/main/inquiry',
					element: <InquiryManagementPage />,
				},
				{
					path: '/main/shipping',
					element: <ShippingPage />,
				},
				{
					/*
						배송 건 추가 · 자세히 · 라벨 만들기 · 라벨 보기 — 모두 목록 위에 덮이는 전체 화면 한 장이다.
						그리는 것은 같은 페이지이고 주소만 다르다(DISPATCH_PLAN §3.7). `new` 는 정적 경로라
						`:dispatchId` 보다 먼저 맞는다.
					*/
					path: '/main/shipping/new',
					element: <ShippingPage />,
				},
				{
					path: '/main/shipping/:dispatchId',
					element: <ShippingPage />,
				},
				{
					path: '/main/shipping/:dispatchId/label/new',
					element: <ShippingPage />,
				},
				{
					path: '/main/shipping/:dispatchId/label',
					element: <ShippingPage />,
				},
				{
					path: '/main/shipping/:dispatchId/label/:shipmentId',
					element: <ShippingPage />,
				},
				{
					path: '/main/box',
					element: <BoxPage />,
				},
				// 납품가 관리 — 거래처 × 제품 규칙 (DISPATCH_PLAN §4). MENU 행은 migrate_dealer_price.sql
				{
					path: '/main/dealer-price',
					element: <DealerPricePage />,
				},
				// 정산 — 부가세·후원금 적립 · 납부 체크 (DISPATCH_PLAN §6). MENU 행은 migrate_settlement.sql(「회계」 폴더 아래)
				{
					path: '/main/settlement',
					element: <SettlementPage />,
				},
				{
					// 상세 페이지는 목록 한 화면(카드 + 타임라인 드로어)으로 합쳤다. 옛 링크는 목록으로.
					path: '/main/inquiry/:inquiryId',
					element: <Navigate to="/main/inquiry" replace />,
				},

				// @PLOP-CODE
			],
		},
		{
			// My Page — FullModalLayout(전체 화면)이라 MainLayout 밖 형제 라우트다.
			// 메뉴 권한을 따르지 않는다(AccessControlGuard.defaultPageUris).
			path: '/main/mypage',
			element: (
				<AccessGuard>
					<AdminMyPage />
				</AccessGuard>
			),
		},
		{
			path: '/main/reset-customer-password',
			element: (
				<AccessGuard>
					<ResetMainCustomerPasswordPage />
				</AccessGuard>
			),
		},
		{
			// 시즌 상품 상세 — 판매 단계(얼리버드·프리오더·재시작)와 이행 로드맵.
			// 시즌 수정과 같은 이유로 형제 라우트다(전체 화면).
			path: '/main/season/:seasonId/product/:seasonProductId',
			element: (
				<AccessGuard>
					<SeasonProductDetailPage />
				</AccessGuard>
			),
		},
	]

	return res
}

export default HomeRoute
